
type TimeUnit = 'year' | 'day' | 'hour' | 'minute' | 'second';

interface TimeToken {
    unit: TimeUnit;
    value: number;
    label: string; // 中文文案
}

interface TimeFormatResult {
    /**
     * 精确的字段
     */
    precise: string;
    /**
     * 不精确的字段
     */
    imprecise: string;
    /**
     * 精确 tokens（给 UI 用）
     */
    preciseTokens: TimeToken[];

    /**
     * 不精确 tokens（给 UI 用）
     */
    impreciseTokens: TimeToken[];

    /**
     * 小时
     */
    hour: number;
    /**
     * 天
     */
    day: number;
    /**
     * 分钟
     */
    minute: number;
    /**
     * 秒
     */
    second: number;
}

/**
 * 时间格式化工具函数， 传入的时间均是以当前时间作为基准
 * @param time - 入参是秒, 秒带小数会取整处理
 * @returns 格式化后的时间对象 
 */
export const formartTimeWithUnitAndChinese = (time: number | string): TimeFormatResult => {
    const defaultResult = {
        hour: 0,
        day: 0,
        minute: 0,
        second: 0,
        preciseTokens: [],
        impreciseTokens: [],
        precise: '0',
        imprecise: '0',
    }

    if (!time) {
        return defaultResult
    }

    if (typeof time === 'string') {
        time = Number(time);

        if (isNaN(time)) {
            console.log('传入的时间非数字', time)
            return defaultResult
        }
    }

    // 取整处理
    time = Math.floor(time);

    // 转换为秒
    const seconds = time;
    // 计算各时间单位
    const year = Math.floor(seconds / (365 * 24 * 60 * 60));
    const day = Math.floor((seconds % (365 * 24 * 60 * 60)) / (24 * 60 * 60));
    const hour = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minute = Math.floor((seconds % (60 * 60)) / 60);
    const second = seconds % 60;

    // 构建精确时间字符串
    let precise = '';

    const unitMap = [
        { unit: 'year', value: year, label: '年' },
        { unit: 'day', value: day, label: '天' },
        { unit: 'hour', value: hour, label: '时' },
        { unit: 'minute', value: minute, label: '分' },
        { unit: 'second', value: second, label: '秒' }
    ] as const;

    let preciseTokens: TimeToken[] = [];

    // 找到第一个非 0 的单位
    const startIndex = unitMap.findIndex(u => u.value > 0);

    // 全是 0
    if (startIndex === -1) {
        precise = '0秒';
        preciseTokens = [{
            unit: 'second',
            value: 0,
            label: '秒',
        }];
    } else {
        precise = unitMap
            .slice(startIndex)
            .map(u => `${u.value}${u.label}`)
            .join('');

        preciseTokens = unitMap
            .slice(startIndex)
            .map(u => ({
                unit: u.unit,
                value: u.value,
                label: u.label,
            }));
    }


    const impreciseRules = [
        {
            match: () => year === 0 && day === 0 && hour === 0 && minute === 0 && second > 0,
            units: ['second']
        },
        {
            match: () => year === 0 && day === 0 && hour === 0 && minute > 0,
            units: ['minute', 'second'],
        },
        {
            match: () => year === 0 && day === 0,
            units: ['hour', 'minute', 'second'],
        },
        {
            match: () => year === 0 && day === 1 && hour === 0,
            units: ['day'],
        },
        {
            match: () => year === 0,
            units: ['day', 'hour'],
        },
        {
            match: () => year === 1 && day === 0,
            units: ['year'],
        },
        {
            match: () => year > 0,
            units: ['year', 'day'],
        },
    ];
    const hitRule = impreciseRules.find(r => r.match())!;

    const impreciseTokens: TimeToken[] = hitRule.units.map(unit => {
        const u = unitMap.find(i => i.unit === unit)!;
        return {
            unit: u.unit,
            value: u.value,
            label: u.label,
        };
    });

    // 构建不精确时间字符串
    const imprecise = impreciseTokens
        .map(t => `${t.value}${t.label}`)
        .join('');

    return {
        hour,
        day,
        minute,
        second,
        precise,
        imprecise,
        preciseTokens,
        impreciseTokens
    };
};
